import { storageService } from '@/services/storage.service';
import { PiniaPluginContext } from 'pinia';
import { watch } from 'vue';

export interface PersistStrategy {
    key?: string;
    storage?: Storage;
    paths?: string[];
    expiration?: number;
}

export interface PersistOptions {
    enabled: true;
    strategies: PersistStrategy[];
}

type Store = PiniaPluginContext['store'];
type PartialState = Partial<Store['$state']>;

declare module 'pinia' {
    export interface DefineStoreOptions<Id extends string, S extends StateTree, G extends unknown, A> {
        persist?: PersistOptions;
    }
}

const setItem = (key: string, value: string, strategy: PersistStrategy, storage: Storage) => {
    if (strategy.expiration) {
        storageService.setItemWithExpiration(key, value, storage, strategy.expiration);
    } else {
        storage.setItem(key, value)
    }
}

const getItem = (key: string, strategy: PersistStrategy, storage: Storage) => {
    if (strategy.expiration) {
        return storageService.getItemWithExpiration(key, storage);
    } else {
        return storage.getItem(key);
    }
}

const updateStorage = (strategy: PersistStrategy, store: Store) => {
    const storage = strategy.storage || sessionStorage
    const storeKey = strategy.key || store.$id

    if (strategy.paths) {
        const partialState = strategy.paths.reduce((finalObj, key) => {
            finalObj[key] = store.$state[key]
            return finalObj
        }, {} as PartialState)

        setItem(storeKey, JSON.stringify(partialState), strategy, storage);
    } else {
        setItem(storeKey, JSON.stringify(store.$state), strategy, storage);
    }
}

export default ({ options, store }: PiniaPluginContext): void => {
    if (options.persist?.enabled) {
        options.persist?.strategies.forEach((strategy) => {
            const storage = strategy.storage || sessionStorage
            const storeKey = strategy.key || store.$id
            const storageResult = getItem(storeKey, strategy, storage);

            if (storageResult) {
                store.$patch(JSON.parse(storageResult))
                updateStorage(strategy, store)
            }
        })

        watch(() => store.$state, () => {
            options.persist?.strategies.forEach((strategy) => {
                updateStorage(strategy, store)
            })
        }, { deep: true })
    }
}